/**
 * Os três atos de uma reunião já marcada: remarcar, registrar que aconteceu, cancelar.
 *
 * **Cancelar exige motivo, e o motivo é do cliente.** Ele vai para a esteira e é o que o cliente
 * lê no lugar da reunião — "cancelada" sozinho deixa a pessoa do outro lado adivinhando se foi
 * ela que fez algo errado.
 *
 * **Registrar a realização é o que destrava a ata.** Não é burocracia: o servidor recusa abrir ata
 * de reunião cancelada, porque uma ata afirmaria que aconteceu algo que não aconteceu.
 *
 * **Remarcar é parcial.** Só o que mudou vai no corpo, e apagar o link é um ato com nome próprio
 * (`limpar_link`) porque `link: null` é ambíguo num corpo parcial — "não mexa" e "apague" não
 * podem ser a mesma coisa.
 */

import { useState } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  DURACAO_MAXIMA_EM_MINUTOS,
  MOTIVO_MAXIMO_DE_CANCELAMENTO,
  MOTIVO_MINIMO_DE_CANCELAMENTO,
  agoraNoCampoDeInstante,
  formatarQuando,
  paraCampoDeInstante,
  paraInstanteComFuso,
  useCancelarReuniao,
  useRegistrarRealizacao,
  useRemarcarReuniao,
  type PedidoDeRemarcacao,
  type Reuniao,
} from "../../nucleo/reuniao";

export interface PropsDasAcoesDaReuniao {
  reuniao: Reuniao;
  /** `reuniao:agendar` — sem ela a tela mostra o estado e nenhum botão. */
  podeAgendar: boolean;
}

type Gesto = "nenhum" | "remarcar" | "realizar" | "cancelar";

export function AcoesDaReuniao({ reuniao, podeAgendar }: PropsDasAcoesDaReuniao) {
  const [gesto, setGesto] = useState<Gesto>("nenhum");

  if (reuniao.estado === "cancelada") {
    return (
      <Aviso tom="atencao" data-testid="reuniao-cancelada">
        <b>
          Esta reunião foi cancelada em{" "}
          <span className="numero-tabular">{formatarQuando(reuniao.cancelada_em) ?? "—"}</span>.
        </b>{" "}
        Reunião cancelada não recebe ata: ela afirmaria que aconteceu algo que não aconteceu. Marque
        outra reunião.
      </Aviso>
    );
  }

  if (!podeAgendar) {
    return (
      <p className="nota-rodape" data-testid="sem-permissao-de-agendar">
        Remarcar, cancelar e registrar a realização ficam com quem agenda as reuniões.
      </p>
    );
  }

  return (
    <div data-testid="acoes-da-reuniao">
      {gesto === "nenhum" ? (
        <div className="acoes-do-console">
          {reuniao.estado === "agendada" ? (
            <Botao onClick={() => setGesto("realizar")} data-testid="pedir-realizacao">
              Registrar que aconteceu
            </Botao>
          ) : null}
          <Botao
            variante="fantasma"
            onClick={() => setGesto("remarcar")}
            data-testid="pedir-remarcacao"
          >
            Remarcar
          </Botao>
          {reuniao.estado === "agendada" ? (
            <Botao
              variante="fantasma"
              onClick={() => setGesto("cancelar")}
              data-testid="pedir-cancelamento"
            >
              Cancelar a reunião
            </Botao>
          ) : null}
        </div>
      ) : null}

      {gesto === "remarcar" ? (
        <Remarcacao reuniao={reuniao} aoFechar={() => setGesto("nenhum")} />
      ) : null}
      {gesto === "realizar" ? (
        <Realizacao reuniao={reuniao} aoFechar={() => setGesto("nenhum")} />
      ) : null}
      {gesto === "cancelar" ? (
        <Cancelamento reuniao={reuniao} aoFechar={() => setGesto("nenhum")} />
      ) : null}
    </div>
  );
}

function Remarcacao({ reuniao, aoFechar }: { reuniao: Reuniao; aoFechar: () => void }) {
  const [quando, setQuando] = useState(paraCampoDeInstante(reuniao.agendada_para));
  const [duracao, setDuracao] = useState(String(reuniao.duracao_min));
  const [link, setLink] = useState(reuniao.link ?? "");
  const [observacao, setObservacao] = useState("");
  /* O erro do motivo só depois de a pessoa passar pelo campo: abrir o painel já em vermelho
     acusa uma falha que ninguém cometeu. O Salvar continua travado enquanto falta o motivo. */
  const [motivoTocado, setMotivoTocado] = useState(false);
  const remarcar = useRemarcarReuniao(reuniao.id);

  const minutos = Number(duracao);
  const linkLimpo = link.trim();
  const linkInseguro = linkLimpo.length > 0 && !linkLimpo.startsWith("https://");

  return (
    <Cartao titulo="Remarcar" data-testid="remarcacao">
      <div className="grade-do-agendamento">
        <Campo
          rotulo="Nova data e hora"
          type="datetime-local"
          value={quando}
          onChange={(evento) => setQuando(evento.target.value)}
          ajuda="Horário de Brasília."
          data-testid="nova-data"
        />
        <Campo
          rotulo="Duração em minutos"
          type="number"
          min={1}
          max={DURACAO_MAXIMA_EM_MINUTOS}
          value={duracao}
          onChange={(evento) => setDuracao(evento.target.value)}
          data-testid="nova-duracao"
        />
      </div>
      <Campo
        rotulo="Link da sala"
        type="url"
        value={link}
        onChange={(evento) => setLink(evento.target.value)}
        erro={linkInseguro ? "O link precisa começar com https://." : undefined}
        ajuda="Apagar o campo remove o link da reunião — o cliente deixa de ver a sala."
        data-testid="novo-link"
      />

      <CampoLongo
        rotulo="Motivo da remarcação"
        value={observacao}
        required
        maxLength={500}
        onChange={(evento) => setObservacao(evento.target.value)}
        onBlur={() => setMotivoTocado(true)}
        ajuda="Obrigatório. O cliente recebe este motivo na notificação e ele fica no histórico."
        erro={motivoTocado && !observacao.trim() ? "Informe o motivo da remarcação." : undefined}
        data-testid="motivo-da-remarcacao"
      />

      {remarcar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-da-remarcacao">
          {mensagemDoConsole(remarcar.error)}
        </Aviso>
      ) : null}

      <div className="acoes-do-console">
        <Botao
          disabled={remarcar.isPending || linkInseguro || !observacao.trim()}
          data-testid="confirmar-remarcacao"
          onClick={() => {
            const instante = paraInstanteComFuso(quando);
            /* Corpo parcial de verdade: só entra o que mudou. Reenviar tudo faria uma correção
               de horário sobrescrever um link que outra pessoa acabou de trocar. */
            const corpo: PedidoDeRemarcacao = { observacao: observacao.trim() };
            /* Compara o INSTANTE, não o texto: a API escreve `+00:00` e o campo devolve `Z` —
               como texto eles nunca são iguais, e o horário ia no corpo em toda remarcação. */
            if (
              instante &&
              new Date(instante).getTime() !== new Date(reuniao.agendada_para).getTime()
            ) {
              corpo.agendada_para = instante;
            }
            if (Number.isInteger(minutos) && minutos !== reuniao.duracao_min) {
              corpo.duracao_min = minutos;
            }
            if (linkLimpo !== (reuniao.link ?? "")) {
              if (linkLimpo) corpo.link = linkLimpo;
              else corpo.limpar_link = true;
            }
            remarcar.mutate(corpo, { onSuccess: aoFechar });
          }}
        >
          {remarcar.isPending ? "Remarcando…" : "Salvar"}
        </Botao>
        <Botao variante="fantasma" disabled={remarcar.isPending} onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </Cartao>
  );
}

function Realizacao({ reuniao, aoFechar }: { reuniao: Reuniao; aoFechar: () => void }) {
  const [quando, setQuando] = useState(agoraNoCampoDeInstante);
  const registrar = useRegistrarRealizacao(reuniao.id);

  return (
    <Cartao titulo="Registrar que a reunião aconteceu" data-testid="realizacao">
      <p className="cartao__texto">
        O registro entra na esteira do cliente e é o que permite escrever a ata desta conversa.
      </p>
      <Campo
        rotulo="Quando aconteceu"
        type="datetime-local"
        value={quando}
        onChange={(evento) => setQuando(evento.target.value)}
        ajuda="Já vem preenchido com agora — o caso de quem registra ao sair da reunião."
        data-testid="quando-aconteceu"
      />
      {registrar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-da-realizacao">
          {mensagemDoConsole(registrar.error)}
        </Aviso>
      ) : null}
      <div className="acoes-do-console">
        <Botao
          disabled={registrar.isPending}
          data-testid="confirmar-realizacao"
          onClick={() =>
            registrar.mutate(paraInstanteComFuso(quando), { onSuccess: aoFechar })
          }
        >
          {registrar.isPending ? "Registrando…" : "Registrar"}
        </Botao>
        <Botao variante="fantasma" disabled={registrar.isPending} onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </Cartao>
  );
}

function Cancelamento({ reuniao, aoFechar }: { reuniao: Reuniao; aoFechar: () => void }) {
  const [motivo, setMotivo] = useState("");
  const cancelar = useCancelarReuniao(reuniao.id);
  const curto = motivo.trim().length < MOTIVO_MINIMO_DE_CANCELAMENTO;

  return (
    <Cartao className="cartao--grave" titulo="Cancelar a reunião" data-testid="cancelamento">
      <p className="cartao__texto">
        O motivo abaixo vai para a esteira e é o que o <b>cliente</b> lê no lugar da reunião.
        Escreva a frase que você diria a ele.
      </p>
      <CampoLongo
        rotulo="Motivo do cancelamento"
        rows={3}
        value={motivo}
        maxLength={MOTIVO_MAXIMO_DE_CANCELAMENTO}
        onChange={(evento) => setMotivo(evento.target.value)}
        data-testid="motivo-do-cancelamento"
      />
      {cancelar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-do-cancelamento">
          {mensagemDoConsole(cancelar.error)}
        </Aviso>
      ) : null}
      <div className="acoes-do-console">
        <Chip estado="atencao">confirmação</Chip>
        <Botao
          disabled={cancelar.isPending || curto}
          data-testid="confirmar-cancelamento"
          onClick={() => cancelar.mutate(motivo.trim(), { onSuccess: aoFechar })}
        >
          {cancelar.isPending ? "Cancelando…" : "Cancelar a reunião"}
        </Botao>
        <Botao variante="fantasma" disabled={cancelar.isPending} onClick={aoFechar}>
          Voltar
        </Botao>
      </div>
    </Cartao>
  );
}
